/**
 * Genome — the structured-cloneable parameter object every shark mesh is grown from,
 * plus its legal ranges, its normaliser, and the cubic bezier the body profile rides on.
 */

import { Rng } from '../core/Rng.ts';
import { rollSharkPalette } from './Palette.ts';

export type Snout = 'pointed' | 'blunt' | 'shovel' | 'hammer' | 'goblin';
export type EyeStyle = 'lateral' | 'stalked' | 'cephalofoil';
export type Pattern = 'solid' | 'countershaded' | 'stripes' | 'spots' | 'mottle' | 'leopard';

export const SNOUTS: readonly Snout[] = ['pointed', 'blunt', 'shovel', 'hammer', 'goblin'];
export const EYE_STYLES: readonly EyeStyle[] = ['lateral', 'stalked', 'cephalofoil'];
export const PATTERNS: readonly Pattern[] = [
  'solid', 'countershaded', 'stripes', 'spots', 'mottle', 'leopard',
];

export interface FinParams {
  height: number;    // voxels
  rake: number;      // 0..1, backward sweep
  chord: number;     // voxels along the spine
  position: number;  // 0..1 along the spine, 0 = nose, 1 = tail tip
}

export interface SharkGenome {
  seed: number;
  length: number;      // voxels along the spine, 24..52
  girth: number;       // max half-width in voxels, 4..11
  /** cubic bezier control points for the body radius profile, nose(0) → tail(1) */
  taperCurve: [number, number, number, number];
  snout: Snout;
  snoutLength: number; // voxels
  dorsalFin: FinParams;
  pectoralFins: FinParams;
  caudalFin: { upperLobe: number; lowerLobe: number; rake: number };
  gillSlits: number;   // 5..7
  eyeStyle: EyeStyle;
  pattern: Pattern;
  palette: [number, number, number];  // hex sRGB: dorsal, flank, ventral
  jawWidth: number;    // voxels
  toothRows: number;   // 1..3

  // --- sculptor extensions (still part of the genome, still seeded) ---
  /** vertical squash of the body cross-section; >1 = laterally compressed (taller than wide) */
  heightRatio: number;      // 1.05 .. 1.55
  /** where the body is thickest, 0 = nose, 1 = tail */
  girthPeak: number;        // 0.30 .. 0.45
  /** thickness of the tail stalk relative to girth */
  peduncle: number;         // 0.14 .. 0.30
  /** second (small) dorsal fin height, 0 disables */
  secondDorsal: number;
  /** pelvic + anal fin size in voxels, 0 disables */
  pelvicFin: number;
  /** downward curve of the spine toward the head, in voxels */
  headDrop: number;         // 0 .. 2
  /** cephalofoil half-span in voxels; only meaningful for snout === 'hammer' */
  foilSpan: number;
  /** pattern feature scale; LOWER = larger blobs = fewer triangles */
  patternScale: number;     // 0.05 .. 0.16
  /** overall world-space scale multiplier applied after voxelisation */
  scale: number;            // 1 for most species, 2 for Megalodon

  /**
   * Selects a hand-tuned model instead of the shared parametric sculptor. Only the tiger
   * has one; everything above is still carried so stats, palette and the lab readouts work
   * uniformly, but TigerSculpt ignores the shape fields and uses its own profile tables.
   */
  model?: 'tiger';
  /** tiger only: voxels along the model's long axis. Cost scales ~res^3. */
  tigerRes?: number;
  /** tiger only: 0 blunt .. 1 acute rostrum */
  tigerSnout?: number;
  /** tiger only: 0 juvenile (strong bars) .. 1 adult (faded) */
  tigerAge?: number;
  /** tiger only: finished nose-to-tail length in world units; sets the voxel size */
  tigerLength?: number;
}

/**
 * Every numeric field's legal range. The fin/caudal entries are shared by all fin slots.
 * Ceilings are sized so the widest legal roll (girth 11) still fits: pectoral span 11 x 2.0,
 * caudal upper lobe near body length for the thresher, jaw across a full 2 x girth head.
 */
export const GENOME_LIMITS: {
  length: [number, number];
  girth: [number, number];
  gillSlits: [number, number];
  snoutLength: [number, number];
  jawWidth: [number, number];
  toothRows: [number, number];
  heightRatio: [number, number];
  girthPeak: [number, number];
  peduncle: [number, number];
  secondDorsal: [number, number];
  pelvicFin: [number, number];
  headDrop: [number, number];
  foilSpan: [number, number];
  patternScale: [number, number];
  scale: [number, number];
  taper: [number, number];
  finHeight: [number, number];
  finRake: [number, number];
  finChord: [number, number];
  finPosition: [number, number];
  caudalLobe: [number, number];
  caudalRake: [number, number];
} = {
  // SPEC §4.1 quotes 24..52 / 4..11 at reference resolution. VOXEL.detailScale rescales the
  // whole roster, so the legal window has to reach below that or every small species clamps
  // back up and silently ignores the scale.
  length: [15, 52],
  girth: [2.4, 11],
  gillSlits: [5, 7],
  snoutLength: [1, 14],
  jawWidth: [1, 22],
  toothRows: [1, 3],
  heightRatio: [1.05, 1.55],
  girthPeak: [0.30, 0.45],
  peduncle: [0.14, 0.30],
  secondDorsal: [0, 12],
  pelvicFin: [0, 12],
  headDrop: [0, 2],
  foilSpan: [0, 26],
  patternScale: [0.05, 0.30],
  scale: [0.25, 4],
  taper: [0.02, 1.6],
  finHeight: [0, 26],
  finRake: [0, 1],
  finChord: [0.5, 26],
  finPosition: [0.05, 0.95],
  caudalLobe: [0, 46],
  caudalRake: [0, 1],
};

/**
 * Cold slate / steel / knocked-back bone. Only used when a genome arrives with an unusable
 * palette; it is a legal colourway in its own right, not a magenta error flag.
 */
const FALLBACK_PALETTE: [number, number, number] = [0x1e2c36, 0x41626d, 0xd6d0c0];

/** Nose narrow, shoulders full around s≈0.35, stalk thin. Matches a real fusiform body. */
const FALLBACK_TAPER: [number, number, number, number] = [0.35, 1.15, 0.55, 0.06];

const FALLBACK_DORSAL: FinParams = { height: 8, rake: 0.45, chord: 6, position: 0.40 };
const FALLBACK_PECTORAL: FinParams = { height: 9, rake: 0.55, chord: 4, position: 0.28 };

function clampTo(v: unknown, range: readonly [number, number], fallback: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  return n < range[0] ? range[0] : n > range[1] ? range[1] : n;
}

/** Grid-defining fields must be whole voxels or the sculptor's dims drift off the SDF. */
function clampInt(v: unknown, range: readonly [number, number], fallback: number): number {
  return Math.round(clampTo(v, range, fallback));
}

function pickEnum<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

function clampHex(v: unknown, fallback: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : fallback;
  return n < 0 ? 0 : n > 0xffffff ? 0xffffff : n;
}

function normFin(v: unknown, fallback: FinParams): FinParams {
  const f = (typeof v === 'object' && v !== null ? v : {}) as Partial<FinParams>;
  const L = GENOME_LIMITS;
  return {
    height: clampTo(f.height, L.finHeight, fallback.height),
    rake: clampTo(f.rake, L.finRake, fallback.rake),
    chord: clampTo(f.chord, L.finChord, fallback.chord),
    position: clampTo(f.position, L.finPosition, fallback.position),
  };
}

/** clamp every field into legal range; returns a new object. Never throws. */
export function normalizeGenome(g: SharkGenome): SharkGenome {
  // Cast through nullable: this is the one entry point that has to survive a genome
  // arriving from a URL param, a save file, or another author's half-built object.
  const src: Partial<SharkGenome> = (g as Partial<SharkGenome> | null | undefined) ?? {};
  const L = GENOME_LIMITS;

  const seed = typeof src.seed === 'number' && Number.isFinite(src.seed)
    ? Math.floor(src.seed) >>> 0
    : 0;

  const length = clampInt(src.length, L.length, 38);
  const girth = clampInt(src.girth, L.girth, 7);

  const t = Array.isArray(src.taperCurve) && src.taperCurve.length >= 4
    ? src.taperCurve
    : FALLBACK_TAPER;
  const taperCurve: [number, number, number, number] = [
    clampTo(t[0], L.taper, FALLBACK_TAPER[0]),
    clampTo(t[1], L.taper, FALLBACK_TAPER[1]),
    clampTo(t[2], L.taper, FALLBACK_TAPER[2]),
    clampTo(t[3], L.taper, FALLBACK_TAPER[3]),
  ];

  const p = Array.isArray(src.palette) && src.palette.length >= 3 ? src.palette : FALLBACK_PALETTE;
  const palette: [number, number, number] = [
    clampHex(p[0], FALLBACK_PALETTE[0]),
    clampHex(p[1], FALLBACK_PALETTE[1]),
    clampHex(p[2], FALLBACK_PALETTE[2]),
  ];

  const snout = pickEnum(src.snout, SNOUTS, 'pointed');
  const caudal = (src.caudalFin ?? {}) as Partial<SharkGenome['caudalFin']>;

  return {
    seed,
    length,
    girth,
    taperCurve,
    snout,
    // A snout can never eat more than a third of the body — past that the head stops
    // reading as a head and the gill/eye placement in Sculpt has nowhere to land.
    snoutLength: clampTo(
      src.snoutLength,
      [L.snoutLength[0], Math.min(L.snoutLength[1], length / 3)],
      Math.max(L.snoutLength[0], length * 0.12),
    ),
    dorsalFin: normFin(src.dorsalFin, FALLBACK_DORSAL),
    pectoralFins: normFin(src.pectoralFins, FALLBACK_PECTORAL),
    caudalFin: {
      upperLobe: clampTo(caudal.upperLobe, L.caudalLobe, girth * 2.0),
      lowerLobe: clampTo(caudal.lowerLobe, L.caudalLobe, girth * 1.2),
      rake: clampTo(caudal.rake, L.caudalRake, 0.5),
    },
    gillSlits: clampInt(src.gillSlits, L.gillSlits, 5),
    eyeStyle: pickEnum(src.eyeStyle, EYE_STYLES, 'lateral'),
    pattern: pickEnum(src.pattern, PATTERNS, 'countershaded'),
    palette,
    // The mouth line is drawn across the head, so it cannot be wider than the head is.
    jawWidth: clampTo(
      src.jawWidth,
      [L.jawWidth[0], Math.min(L.jawWidth[1], girth * 2)],
      girth * 1.1,
    ),
    toothRows: clampInt(src.toothRows, L.toothRows, 2),
    heightRatio: clampTo(src.heightRatio, L.heightRatio, 1.2),
    girthPeak: clampTo(src.girthPeak, L.girthPeak, 0.36),
    peduncle: clampTo(src.peduncle, L.peduncle, 0.2),
    secondDorsal: clampTo(src.secondDorsal, L.secondDorsal, 0),
    pelvicFin: clampTo(src.pelvicFin, L.pelvicFin, 0),
    headDrop: clampTo(src.headDrop, L.headDrop, 0.6),
    // foilSpan is meaningless off a hammer head and would otherwise inflate the grid dims.
    foilSpan: snout === 'hammer' ? clampTo(src.foilSpan, L.foilSpan, girth * 2) : 0,
    patternScale: clampTo(src.patternScale, L.patternScale, 0.09),
    scale: clampTo(src.scale, L.scale, 1),
  };
}

/** fully random but legal genome — used by the lab's "random" button */
export function randomGenome(seed: number): SharkGenome {
  const rng = new Rng(seed >>> 0);

  const snout = rng.pick(SNOUTS);
  const length = rng.int(26, 51);
  // Girth is derived from length rather than rolled independently: an independent roll
  // produces sausages and eels about a third of the time.
  const girth = Math.max(4, Math.min(11, Math.round(length * rng.range(0.13, 0.24))));

  // Snout proportion is the strongest silhouette cue, so each shape gets its own band.
  const snoutFrac =
    snout === 'goblin' ? rng.range(0.16, 0.24)
    : snout === 'pointed' ? rng.range(0.10, 0.16)
    : snout === 'shovel' ? rng.range(0.08, 0.12)
    : snout === 'hammer' ? rng.range(0.05, 0.08)
    : rng.range(0.05, 0.09);

  const upperLobe = girth * rng.range(1.6, 2.6);

  const g: SharkGenome = {
    seed: seed >>> 0,
    length,
    girth,
    taperCurve: [
      rng.range(0.22, 0.45),
      rng.range(1.00, 1.30),
      rng.range(0.45, 0.72),
      rng.range(0.04, 0.12),
    ],
    snout,
    snoutLength: length * snoutFrac,
    dorsalFin: {
      height: girth * rng.range(0.9, 1.7),
      rake: rng.range(0.25, 0.70),
      chord: length * rng.range(0.10, 0.18),
      position: rng.range(0.34, 0.46),
    },
    pectoralFins: {
      height: girth * rng.range(1.1, 2.0),
      rake: rng.range(0.35, 0.75),
      chord: length * rng.range(0.08, 0.13),
      position: rng.range(0.24, 0.32),
    },
    caudalFin: {
      upperLobe,
      lowerLobe: upperLobe * rng.range(0.45, 0.80),
      rake: rng.range(0.30, 0.75),
    },
    gillSlits: rng.int(5, 8),
    // A cephalofoil is a hammer's eye placement by definition; anything else is a broken head.
    eyeStyle: snout === 'hammer' ? 'cephalofoil' : (rng.bool(0.15) ? 'stalked' : 'lateral'),
    pattern: rng.pick(PATTERNS),
    palette: rollSharkPalette(rng),
    jawWidth: girth * rng.range(0.9, 1.4),
    toothRows: rng.int(1, 4),
    heightRatio: rng.range(1.08, 1.50),
    girthPeak: rng.range(0.30, 0.45),
    peduncle: rng.range(0.15, 0.28),
    secondDorsal: rng.bool(0.65) ? girth * rng.range(0.25, 0.50) : 0,
    pelvicFin: rng.bool(0.8) ? girth * rng.range(0.35, 0.70) : 0,
    headDrop: rng.range(0, 1.6),
    foilSpan: snout === 'hammer' ? girth * rng.range(1.6, 2.3) : 0,
    // Kept in the low half of the legal band: high-frequency pattern is what blows the
    // 900-triangle gate, because it breaks the mesher's colour runs.
    patternScale: rng.range(0.06, 0.13),
    scale: 1,
  };

  return normalizeGenome(g);
}

/** cubic bezier scalar evaluation used for taperCurve */
export function bezier4(c: readonly [number, number, number, number], t: number): number {
  const s = t < 0 ? 0 : t > 1 ? 1 : t;
  const u = 1 - s;
  const uu = u * u;
  const ss = s * s;
  return uu * u * c[0] + 3 * uu * s * c[1] + 3 * u * ss * c[2] + ss * s * c[3];
}
